import { SelectionMode } from "@xyflow/react";
import type { CanvasCursorTool } from "./canvas-controls.types";

// ---------------------------------------------------------------------------
// Canvas controls configuration
//
// All behavioural constants for mouse, touchpad, keyboard, and grid controls
// live here. Handler logic stays in CanvasPage.tsx; only change *what*
// triggers something here.
// ---------------------------------------------------------------------------

export const CANVAS_PAN = {
  /**
   * Mouse buttons that trigger panning when dragged.
   * 1 = middle mouse button, 2 = right mouse button.
   */
  mouseButtons: [1, 2] as number[],
  /** Two-finger scroll (touchpad) and scroll wheel pan the viewport. */
  onScroll: true,
} as const;

export const CANVAS_ZOOM = {
  /** Scroll wheel alone does NOT zoom (it pans instead via CANVAS_PAN.onScroll). */
  onScroll: false,
  /** Pinch gesture on touchpad zooms. */
  onPinch: true,
  /** Double-click does NOT zoom. */
  onDoubleClick: false,
  minZoom: 0.05,
  maxZoom: 4,
} as const;

export const CANVAS_SELECTION = {
  mode: SelectionMode.Partial,
  /** Modifier keys that toggle individual node selection (Cmd, Ctrl, or Shift). */
  multiSelectionKeys: ["Meta", "Control", "Shift"],
} as const;

/** React Flow interaction flags per top-right cursor tool (Arrow vs Pointer). */
export interface CanvasCursorInteraction {
  panOnDragMouseButtons: number[];
  selectionOnDrag: boolean;
  elementsSelectable: boolean;
  nodesDraggable: boolean;
  nodesConnectable: boolean;
  /** Pane click may select sections / change selection (Arrow only). */
  paneSelectsOnClick: boolean;
}

export function getCanvasCursorInteraction(
  tool: CanvasCursorTool,
): CanvasCursorInteraction {
  if (tool === "hand") {
    return {
      /** Left, middle, and right drag pan — Figma Pointer / Hand tool. */
      panOnDragMouseButtons: [0, 1, 2],
      selectionOnDrag: false,
      elementsSelectable: false,
      nodesDraggable: false,
      nodesConnectable: false,
      paneSelectsOnClick: false,
    };
  }

  return {
    panOnDragMouseButtons: [...CANVAS_PAN.mouseButtons],
    selectionOnDrag: true,
    elementsSelectable: true,
    nodesDraggable: true,
    nodesConnectable: true,
    paneSelectsOnClick: true,
  };
}

/**
 * THE canvas lattice — the one grid number in the system. Positions,
 * dimensions, gaps, and endpoints all snap to multiples of it; the
 * rendered grid draws at it (every visible line is a real snap target).
 * Ruling: docs/features/2026-08-10-canvas-objects-redesign/
 * canvas-lattice-ruling.md (backend repo).
 */
export const LATTICE = 16;

export const CANVAS_SNAP = {
  enabled: true,
  grid: [LATTICE, LATTICE] as [number, number],
} as const;

/** Dot grid rendering — matches dashboard-comp / showcase `canvas.css`. */
export const CANVAS_GRID = {
  /** Dot diameter at zoom 1 (1.2px radius). */
  dotSize: 2.4,
  /** Glow-layer dot diameter at zoom 1 (1.4px radius). */
  dotGlowSize: 2.8,
} as const;

/** Spacing between nodes (vertical and horizontal). Matches grid for consistent layout. */
export const CANVAS_SPACING = {
  nodeGap: LATTICE,
} as const;

/**
 * Snap a value to the nearest multiple of `step`.
 * Shared across all canvas placement utilities so there is a single source
 * of truth for the grid-snap math.
 */
export const snapToGrid = (val: number, step: number): number =>
  Math.round(val / step) * step;

export const CANVAS_KEYS = {
  // Quick chat owns C; comment moved to the unused Q.
  comment: "q",
  // The Cursor popover's Hand, flag-ignorant: the legacy controls bar's
  // cursor toggle is the same tool (ROUND3 §4). Nothing else binds H.
  hand: "h",
  quickChat: "c",
  voiceInput: "v",
  addTextLabel: "t",
  addNote: "n",
  addDoc: "d",
  addLink: "l",
  uploadFile: "u",
  addWidgets: "w",
  addShapes: "p",
  addSection: "s",
  cancel: "Escape",
  delete: ["Delete", "Backspace"] as string[],
  stopVoiceRecording: [" ", "Enter"] as string[],
  undo: "z",
  redo: "z",
  zoomIn: "=",
  zoomOut: "-",
  zoomToFit: "1",
  toggleExplorer: "e",
} as const;

/**
 * Every tool shortcut except comment, each with the gates its branch in
 * `useCanvasKeyboardShortcuts` applies. The branches are the source of truth:
 * `exactKey` marks the ones comparing `event.key` unlowercased (an uppercase
 * press misses them), `noShift` an explicit `!event.shiftKey`, `needsEdit` the
 * `canEdit` check.
 */
interface NonCommentToolKey {
  key: string;
  exactKey?: boolean;
  noShift?: boolean;
  needsEdit?: boolean;
}

const NON_COMMENT_TOOL_KEYS: NonCommentToolKey[] = [
  { key: CANVAS_KEYS.quickChat, exactKey: true, needsEdit: true },
  { key: CANVAS_KEYS.voiceInput, exactKey: true, needsEdit: true },
  { key: CANVAS_KEYS.addTextLabel, exactKey: true, needsEdit: true },
  { key: CANVAS_KEYS.addNote, needsEdit: true },
  { key: CANVAS_KEYS.addDoc, exactKey: true, needsEdit: true },
  { key: CANVAS_KEYS.addLink, needsEdit: true },
  { key: CANVAS_KEYS.uploadFile, noShift: true, needsEdit: true },
  { key: CANVAS_KEYS.addWidgets, needsEdit: true },
  { key: CANVAS_KEYS.addShapes, exactKey: true, needsEdit: true },
  { key: CANVAS_KEYS.addSection, noShift: true, needsEdit: true },
  { key: CANVAS_KEYS.toggleExplorer, exactKey: true, noShift: true },
];

/**
 * Does this keypress arm a tool other than comment placement?
 *
 * Comment placement covers the whole board with a click-capturing overlay, so
 * every other tool shortcut has to disarm it first — otherwise the click meant
 * for the tool just armed drops a pin instead. Modified presses are shortcuts
 * of another kind (copy, paste, zoom) and arm nothing; a press its branch would
 * refuse (no edit rights, wrong case, shifted) arms nothing either, and must
 * leave an armed comment alone.
 */
export function armsNonCommentTool(
  event: {
    key: string;
    metaKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
  },
  { canEdit }: { canEdit: boolean },
): boolean {
  if (event.metaKey || event.ctrlKey) return false;
  return NON_COMMENT_TOOL_KEYS.some(
    (tool) =>
      (tool.exactKey ? event.key : event.key.toLowerCase()) === tool.key &&
      !(tool.noShift && event.shiftKey) &&
      !(tool.needsEdit && !canEdit),
  );
}
